import * as React from "react";
import * as Yup from "yup";

import Dialog from "@mui/material/Dialog";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { useFormik, Form, FormikProvider } from "formik";

import PropTypes from "prop-types";
import { getFirestore, collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import CloseButton from "../components/CloseButton";
import ProductForm from "./ProductForm";

const db = getFirestore();
const storage = getStorage();

const steps = ["Product details"];

export default function AddProduct(props) {
  const [activeStep, setActiveStep] = React.useState(0);

  // Front End validation schema using Yup
  const ProductSchema = Yup.object().shape({
    productName: Yup.string().required("Product name is required"),
    category: Yup.string().required("Category is required"),
    qteStock: Yup.number().required("Quantity in stock is required"),
    originalPrice: Yup.number().required("Original price is required"),
    ourPrice: Yup.number().required("Our price is required"),
    colors: Yup.string().required("Colors are required"),
  });

  // Product state as an object
  const [productData, setProductData] = React.useState({
    productName: "",
    originalPrice: "",
    category: "",
    qteStock: "",
    gender: "",
    ourPrice: "",
    colors: "",
    profilePic: "/static/illustrations/product.jpg",
  });

  // Clear state after closing the dialog
  const clearState = () => {
    setProductData({
      productName: "",
      originalPrice: "",
      category: "",
      qteStock: "",
      gender: "",
      ourPrice: "",
      colors: "",
      profilePic: "/static/illustrations/product.jpg",
    });
  };

  // Formik initialization and validation
  const formik = useFormik({
    initialValues: productData,
    enableReinitialize: true,
    validationSchema: ProductSchema,
  });

  // Function to upload product image to Firebase Storage
  // Function to upload product image to Firebase Storage
const uploadImage = async (imageFile) => {
  const storageRef = ref(storage, `products/${imageFile.name}`);
  await uploadBytes(storageRef, imageFile); // Wait for upload to complete
  return getDownloadURL(storageRef); // Directly return the promise from getDownloadURL
};


  // Function to handle product submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      let imageUrl = productData.profilePic;

      if (productData.profilePic instanceof File) {
        imageUrl = await uploadImage(productData.profilePic);
      }

      const productToSave = { ...productData, profilePic: imageUrl };

      if (!props.edit) {
        await addDoc(collection(db, "products"), productToSave);
      } else {
        const productRef = doc(db, "products", props.products._id);
        await updateDoc(productRef, productToSave);
      }
    } catch (error) {
      console.error("Error saving product: ", error);
    }

    setActiveStep(activeStep + 1);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <ProductForm formData={productData} setFormData={setProductData} />
        );
      default:
        throw new Error("Unknown step");
    }
  }

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleClose = () => {
    props.toggle();
    clearState();
    setTimeout(() => setActiveStep(0), 500);
  };

  return (
    <div>
      <Dialog
        open={props.isOpen}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            props.toggle();
            setTimeout(() => setActiveStep(0), 500);
          }
        }}
      >
        <CloseButton onClose={handleClose} />
        <Container component="main" maxWidth="xl">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 1,
              marginBottom: 2,
              marginLeft: 4,
              marginRight: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="h4" gutterBottom>
              New Product
            </Typography>

            <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === steps.length ? (
              <>
                <Typography variant="h5" gutterBottom>
                  The product has been successfully added!
                </Typography>
                <Button onClick={handleClose} sx={{ mt: 3, ml: 1 }}>
                  Done!!
                </Button>
              </>
            ) : (
              <FormikProvider value={formik}>
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  {getStepContent(activeStep)}
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {activeStep !== 0 && (
                      <Button onClick={handleBack} sx={{ mt: 3, ml: 1 }}>
                        Back
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      onClick={
                        activeStep === steps.length - 1
                          ? handleSubmit
                          : handleNext
                      }
                      sx={{ mt: 3, ml: 1 }}
                    >
                      {activeStep === steps.length - 1 ? "Save" : "Next"}
                    </Button>
                  </Box>
                </Form>
              </FormikProvider>
            )}
          </Box>
        </Container>
      </Dialog>
    </div>
  );
}

AddProduct.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};


